/**
 * Service-mapping data model, reproduced from the Signpost Directus schema
 * (Directus 10.8.3 / MySQL, snapshot taken 2026-08-24).
 *
 * Everything lives in localStorage — see services/store.ts. Nothing here reaches
 * Directus, Supabase, or any other production system.
 *
 * FIDELITY NOTES — where this model deliberately differs from Directus:
 *
 * 1. Directus keeps translations in sibling tables (articles_translations_1,
 *    providers_n_translations_1, …) joined on languages.code. We collapse each
 *    into a `translations: Record<locale, …>` map on the entity, matching the
 *    approach already used by the local CMS. Same information, far less
 *    plumbing, and it keeps a service and its translations in one object.
 *
 * 2. M2M junction tables (articles_service_categories, articles_populations,
 *    articles_accessibility, articles_services_subcategories_1,
 *    countries_n_languages) become
 *    plain id arrays on the owning entity. The junctions carry no data of their
 *    own beyond an optional `sort`, so they add nothing as separate records.
 *
 * 3. Directus ids are auto-increment integers. We use ULIDs, consistent with
 *    the rest of the local CMS, and keep `oldid` for the legacy-id column that
 *    several Directus tables carry.
 *
 * 4. `articles` is named `services` here. Its Directus translations label it
 *    "Services"/"Servicios"/"Prestations de service" and its icon is
 *    location_on — "articles" is a legacy table name, not what it holds.
 *
 * 5. Bot/AI config, page content, site config, social-post mirrors and
 *    Directus system collections are out of scope: this is the service-mapping
 *    CMS, not the whole instance.
 */

// ── Localization ─────────────────────────────────────────────────────────────

/** Mirrors Directus `languages` (pk: code). */
export interface Language {
  /** e.g. "en-US". Primary key in Directus. */
  code: string
  name: string
  direction: "ltr" | "rtl"
  /** Directus stores a JSON tag list for Transifex locale mapping. */
  transifex?: string[]
  sort?: number
}

/** One locale's values for a translatable entity. */
/**
 * Service publish state, from the Directus `articles.status` dropdown.
 *
 * Directus's choices and its display labels disagree slightly — the options list
 * has "ready for translation" while the label list has "ReadyforTranslation".
 * We take the option values, which are what actually get stored.
 */
export type ServiceStatus =
  | "draft"
  | "ready for translation"
  | "Translated"
  | "published"
  | "archived"
  | "private"

export interface Translated {
  locale: string
  name: string
  /** HTML. Services and providers both have rich-text descriptions. */
  description?: string
  /** Services carry a translated address; other entities do not. */
  address?: string
  /** Markdown definition, used by taxonomy entities. */
  definition?: string
  /**
   * Whether a human has checked this locale's wording. Mirrors the article
   * CMS's per-locale reviewed flag; machine translation starts unreviewed.
   */
  reviewed?: boolean
  /**
   * Publication status for THIS locale.
   *
   * Undefined falls back to the record's own status, so a service translated
   * before this field existed keeps behaving as it did. Set it to hold one
   * locale back — an unreviewed translation should not go live just because
   * the source language did.
   */
  status?: ServiceStatus
  updatedAt: number
}

// ── Geography ────────────────────────────────────────────────────────────────

/** Mirrors `instances_n` — which website hosts a country's data. */
export interface Instance {
  id: string
  name: string
  oldid?: number
  status?: "draft" | "published" | "archived"
  createdAt: number
  updatedAt: number
}

/** Mirrors `countries_n`. The top of the geography hierarchy. */
export interface Country {
  id: string
  name: string
  /** FK → instances_n */
  instanceId?: string
  /*
   * A site's OWN language list lives on Site (see site-config FIDELITY NOTE
   * 1). These two stay because the service map still needs them: they are
   * what stops a language being deleted while a country references it (see
   * findReferences).
   */
  /** Default language code, FK → languages.code */
  defaultLanguage?: string
  /** The locales this country publishes in. */
  languageCodes: string[]
  /** Public site base url; Directus uses it to build article links. */
  site?: string
  zendeskUrl?: string
  zendeskCategory?: number
  oldid?: number
  translations: Record<string, Translated>
  createdAt: number
  updatedAt: number
}

/** Mirrors `regions_n`. Second level: region within a country. */
export interface Region {
  id: string
  name: string
  /** FK → countries_n */
  countryId: string
  oldid?: number
  translations: Record<string, Translated>
  createdAt: number
  updatedAt: number
}

/**
 * Mirrors `cities_n`. Third level.
 *
 * Directus requires BOTH country and region on a city, and the region dropdown
 * is filtered by the selected country — so a city is always fully qualified.
 */
export interface City {
  id: string
  name: string
  countryId: string
  regionId: string
  oldid?: number
  translations: Record<string, Translated>
  createdAt: number
  updatedAt: number
}

// ── Taxonomy ─────────────────────────────────────────────────────────────────

/** Mirrors `provider_categories_n`. */
export interface ProviderCategory {
  id: string
  name: string
  description?: string
  /** Directus has a country FK, though it is hidden in the UI. */
  countryId?: string
  oldid?: number
  translations: Record<string, Translated>
  createdAt: number
  updatedAt: number
}

/** Mirrors `service_categories`. */
export interface ServiceCategory {
  id: string
  name: string
  description?: string
  /** Hex colour, chosen from a fixed Directus palette. */
  color?: string
  /** Material icon name. */
  icon?: string
  status: "draft" | "published" | "archived"
  sort?: number
  translations: Record<string, Translated>
  createdAt: number
  updatedAt: number
}

/**
 * A free-form tag.
 *
 * Deliberately NOT the Directus `services_subcategories` shape: that table
 * hangs subcategories off categories many-to-many, which proved more structure
 * than the content needed. Tags are a single flat vocabulary with no parent, so
 * a service picks categories and tags independently.
 */
export interface Tag {
  id: string
  name: string
  description?: string
  translations: Record<string, Translated>
  createdAt: number
  updatedAt: number
}

/** Mirrors `populations` — the population a service serves. */
export interface Population {
  id: string
  /** Directus column is `Population_Served`. */
  name: string
  /** Markdown. */
  definition?: string
  visible?: boolean
  translations: Record<string, Translated>
  createdAt: number
  updatedAt: number
}

/** Mirrors `accessibility`. */
export interface Accessibility {
  id: string
  /** Directus column is `Accessibility_Name`. */
  name: string
  /** Markdown. */
  definition?: string
  translations: Record<string, Translated>
  createdAt: number
  updatedAt: number
}

// ── Providers ────────────────────────────────────────────────────────────────

/** Mirrors `providers_n` — the organisation delivering services. */
export interface Provider {
  id: string
  name: string
  /** HTML. */
  description?: string
  address?: string
  /** FK → countries_n. Required in Directus. */
  countryId: string
  /** FK → provider_categories_n. Required in Directus. */
  categoryId?: string
  status: "Active" | "Not Active"
  /** Directus `expiration` (date). ISO yyyy-mm-dd. */
  expiration?: string
  oldid?: number
  translations: Record<string, Translated>
  createdAt: number
  updatedAt: number
}

// ── Services ─────────────────────────────────────────────────────────────────

/** One row of `articles.addHours` (JSON list). */
export interface ServiceHours {
  /** One of HOUR_DAYS — a closed set, not free text. */
  day: HourDay
  /** "HH:mm", 24-hour. */
  open: string
  /** "HH:mm", 24-hour. */
  close: string
}

/** One row of `articles.contactInfo` (JSON list). */
export interface ContactInfoEntry {
  /** Directus restricts this to a fixed channel list; see CONTACT_CHANNELS. */
  channel: string
  contactDetails: string
}

/** One row of `articles.form` (JSON list) — consent-form documents. */
export interface ConsentFormDoc {
  documentName?: string
  documentUrl?: string
}

/**
 * Mirrors `articles` — the service itself, and the centre of this model.
 *
 * Directus groups its ~40 fields into collapsible panels
 * (administrativeInformation, publicContactInformation, serviceHours,
 * consentForm, uploadFiles); those groups are presentation-only and carry no
 * data, so they are flattened here and reconstructed by the editor UI.
 */
export interface Service {
  id: string

  // Identity / status
  status: ServiceStatus
  /** Directus keeps an untranslated `name`, hidden in the UI in favour of translations. */
  name?: string
  /** Untranslated HTML description; likewise hidden in favour of translations. */
  description?: string

  // Geography — country required, region/city progressively filtered
  countryId?: string
  regionId?: string
  cityId?: string

  // Ownership
  providerId?: string

  // Taxonomy — categories and tags are independent vocabularies
  categoryIds: string[]
  tagIds: string[]
  populationIds: string[]
  accessibilityIds: string[]

  // Location
  /**
   * Whether the service is somewhere a person can walk into.
   *
   * Maps to upstream `hasLocation` (signpost-monorepo lib/types.data.ts:67).
   * Renamed for clarity; upstream only passes it through to search results and
   * never branches on it, so the rename costs nothing on migration.
   */
  physicalLocation?: boolean
  address?: string
  lat?: number
  lon?: number
  /** Directus `location` is a geometry.Point; kept as a lon/lat pair. */
  point?: { lon: number; lat: number }

  // Hours
  alwaysOpen?: boolean
  /** Ignored by Directus when alwaysOpen is true (the field is hidden). */
  hours: ServiceHours[]

  // Public contact
  contactInfo: ContactInfoEntry[]

  // Administrative contacts — primary and secondary focal points
  contactName?: string
  contactLastName?: string
  contactTitle?: string
  contactEmail?: string
  contactPhone?: string
  secondaryName?: string
  secondaryLastName?: string
  secondaryTitle?: string
  secondaryEmail?: string
  secondaryPhone?: string

  // Consent form
  consentForms: ConsentFormDoc[]

  // Media — Directus FKs into directus_files; we store urls/ids as text
  headerImage?: string
  files?: string

  // Provenance
  /** Directus default is "Manual"; the other observed value is "Zendesk". */
  source?: string
  oldid?: number
  transifexStatus?: number

  translations: Record<string, Translated>
  createdAt: number
  updatedAt: number
}

// ── Store ────────────────────────────────────────────────────────────────────

/** The whole service-mapping dataset, serialized to localStorage as one blob. */
export interface ServiceStore {
  version: 1
  defaultLocale: string
  languages: Record<string, Language>
  instances: Record<string, Instance>
  countries: Record<string, Country>
  regions: Record<string, Region>
  cities: Record<string, City>
  providerCategories: Record<string, ProviderCategory>
  serviceCategories: Record<string, ServiceCategory>
  tags: Record<string, Tag>
  populations: Record<string, Population>
  accessibility: Record<string, Accessibility>
  providers: Record<string, Provider>
  services: Record<string, Service>
}

/** Entity buckets, used for generic lookups and the agent tool surface. */
export type EntityKind =
  | "language"
  | "instance"
  | "country"
  | "region"
  | "city"
  | "providerCategory"
  | "serviceCategory"
  | "tag"
  | "population"
  | "accessibility"
  | "provider"
  | "service"

/** Maps an EntityKind to its key in ServiceStore. */
export const BUCKET: Record<EntityKind, keyof ServiceStore> = {
  language: "languages",
  instance: "instances",
  country: "countries",
  region: "regions",
  city: "cities",
  providerCategory: "providerCategories",
  serviceCategory: "serviceCategories",
  tag: "tags",
  population: "populations",
  accessibility: "accessibility",
  provider: "providers",
  service: "services",
}

// ── Directus-derived option lists ────────────────────────────────────────────
// Copied verbatim from the schema's interface `options.choices` so the local UI
// offers exactly the same values the production instance accepts.

/** From `articles.contactInfo` → channel dropdown. */
export const CONTACT_CHANNELS = [
  "Email", "Facebook", "Instagram", "Linked In", "Phone", "Signal", "Skype",
  "SMS", "Telegram", "Tik Tok", "Twitter", "Viber", "Website", "Whatsapp",
] as const

/** From `articles.addHours` → Day dropdown. Trailing space on the first entry is Directus's. */
export type HourDay =
  | "Monday to Friday "
  | "Sunday" | "Monday" | "Tuesday" | "Wednesday"
  | "Thursday" | "Friday" | "Saturday"

export const HOUR_DAYS = [
  "Monday to Friday ", "Sunday", "Monday", "Tuesday", "Wednesday",
  "Thursday", "Friday", "Saturday",
] as const satisfies readonly HourDay[]

/**
 * Maps loose day text onto a canonical HOUR_DAYS value.
 *
 * Exists because the stored value for weekdays is "Monday to Friday " WITH a
 * trailing space (a quirk of the source schema), and because imports and
 * agents produce "mon", "Mondays", "Mon-Fri" and similar. Returns null when the
 * input cannot be matched, so callers reject rather than invent a day.
 */
export function normalizeHourDay(input: string): HourDay | null {
  const raw = String(input ?? "").trim().toLowerCase()
  if (!raw) return null

  const weekday: Record<string, HourDay> = {
    sun: "Sunday", mon: "Monday", tue: "Tuesday", tues: "Tuesday", wed: "Wednesday",
    thu: "Thursday", thur: "Thursday", thurs: "Thursday", fri: "Friday", sat: "Saturday",
  }

  // Ranges and collective terms all mean the weekday block.
  const compact = raw.replace(/[\s.]/g, "")
  if (
    /^mon(day)?(to|-|–|—|through|thru)fri(day)?$/.test(compact) ||
    ["weekdays", "weekday", "m-f", "mf", "monfri"].includes(compact)
  ) {
    return "Monday to Friday "
  }

  const stripped = raw.replace(/s$/, "").replace(/[^a-z]/g, "")
  if (weekday[stripped]) return weekday[stripped]
  const prefix = stripped.slice(0, 3)
  if (weekday[prefix]) return weekday[prefix]

  return null
}

/** Validates and normalises "HH:mm"; returns null when unusable. */
export function normalizeTime(input: string): string | null {
  const raw = String(input ?? "").trim()
  if (!raw) return null

  // Accept "9:00", "09:00", "9am", "5:30 PM", "1700".
  const ampm = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*([ap])\.?m\.?$/i)
  if (ampm) {
    let h = Number(ampm[1])
    const m = Number(ampm[2] ?? 0)
    if (h < 1 || h > 12 || m > 59) return null
    if (ampm[3].toLowerCase() === "p" && h !== 12) h += 12
    if (ampm[3].toLowerCase() === "a" && h === 12) h = 0
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
  }

  const hm = raw.match(/^(\d{1,2}):(\d{2})$/)
  if (hm) {
    const h = Number(hm[1])
    const m = Number(hm[2])
    if (h > 23 || m > 59) return null
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
  }

  const digits = raw.match(/^(\d{3,4})$/)
  if (digits) {
    const v = digits[1].padStart(4, "0")
    const h = Number(v.slice(0, 2))
    const m = Number(v.slice(2))
    if (h > 23 || m > 59) return null
    return `${v.slice(0, 2)}:${v.slice(2)}`
  }

  return null
}

/** From `articles.status`. */
export const SERVICE_STATUSES: ServiceStatus[] = [
  "draft", "ready for translation", "Translated", "published", "archived", "private",
]

/** From `providers_n.status`. */
export const PROVIDER_STATUSES = ["Active", "Not Active"] as const

// Icons the public map can draw; anything else renders a blank pin. Keep in sync with lib/components/map.tsx in the monorepo
export const CATEGORY_ICONS = [
  "home",
  "volunteer_activism",
  "school",
  "payments",
  "food_bank",
  "medical_services",
  "gavel",
  "psychology",
  "groups",
  "woman",
  "work_outline",
  "live_help",
  "azm",
] as const

export type CategoryIcon = typeof CATEGORY_ICONS[number]

/** From `service_categories.Color` presets. */
export const CATEGORY_COLORS = [
  "#FFA39E", "#FFBB96", "#FFD591", "#FFE58F", "#FFFB8F", "#EAFF8F",
  "#B7EB8F", "#87E8DE", "#91CAFF", "#ADC6FF", "#D3ADF7", "#FFADD2",
  "#FF7875", "#FF9C6E", "#FFC069", "#FFD666", "#FFF566", "#D3F261",
  "#95DE64", "#5CDBD3", "#69B1FF", "#85A5FF", "#B37FEB", "#FF85C0",
] as const

export function emptyServiceStore(defaultLocale = "en-US"): ServiceStore {
  return {
    version: 1,
    defaultLocale,
    // The default locale must exist as a real language, or the source language
    // is unselectable and nothing can be written in it.
    languages: {
      [defaultLocale]: { code: defaultLocale, name: defaultLocale, direction: "ltr" },
    },
    instances: {},
    countries: {},
    regions: {},
    cities: {},
    providerCategories: {},
    serviceCategories: {},
    tags: {},
    populations: {},
    accessibility: {},
    providers: {},
    services: {},
  }
}

/** Best-available translation, falling back through locale → default → any. */
export function pickTranslated(
  entity: { translations?: Record<string, Translated> },
  locale: string,
  defaultLocale: string
): Translated | null {
  const t = entity.translations
  if (!t) return null
  return t[locale] ?? t[defaultLocale] ?? Object.values(t)[0] ?? null
}

/**
 * Display name for any entity: the requested locale's translation if present,
 * else the untranslated `name` column, else a placeholder.
 */
/**
 * Publication status of one locale.
 *
 * Falls back to the service's own status when the locale has no explicit one,
 * so records predating per-locale status behave exactly as before.
 */
export function serviceStatusFor(
  service: { status: ServiceStatus; translations?: Record<string, Translated> },
  locale: string
): ServiceStatus {
  return service.translations?.[locale]?.status ?? service.status ?? "draft"
}

/** Groups a service's locales by publication status, for badges and audits. */
export function localesByStatus(
  service: { status: ServiceStatus; translations?: Record<string, Translated> }
): Record<string, string[]> {
  const out: Record<string, string[]> = {}
  for (const locale of Object.keys(service.translations ?? {})) {
    const st = serviceStatusFor(service, locale)
    ;(out[st] ||= []).push(locale)
  }
  return out
}

export function displayName(
  entity: { name?: string; translations?: Record<string, Translated> },
  locale: string,
  defaultLocale: string
): string {
  return pickTranslated(entity, locale, defaultLocale)?.name || entity.name || "(untitled)"
}
